import { type FC, memo, useState, useEffect, useRef } from "react";
import translate from "src/lib/translate";
import type { LeapChatFileRef } from "./state/types";

const PREVIEW_SIZE = 300;
const MAX_GRID_COUNT = 4;
import { DocumentIcon, fileSizeString } from "@9amhealth/shared";
import FilesCubit from "src/state/FilesCubit/FilesCubit";
import { fileState } from "src/state/state";
import {
  MessageFilesWrap,
  MessageFilesGrid,
  MessageFileCell,
  MessageFileFallback,
  MessageFileSpinner
} from "./leapChat.styles";

export { DocumentIcon };

const MessageFiles: FC<{ files: LeapChatFileRef[] }> = memo(({ files }) => {
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    let pending = files.length;

    for (const file of files) {
      fileState
        .loadPreview(file.fileId, PREVIEW_SIZE, PREVIEW_SIZE)
        .then((url) => {
          if (!cancelled && url) {
            setPreviews((prev) => ({ ...prev, [file.fileId]: url }));
          }
        })
        .catch(() => {})
        .finally(() => {
          pending--;
          if (!cancelled && pending <= 0) {
            setIsLoading(false);
          }
        });
    }

    return () => {
      cancelled = true;
    };
  }, [isVisible, files]);

  const count = files.length;

  return (
    <MessageFilesWrap data-message-files="" ref={wrapRef}>
      <MessageFilesGrid data-count={Math.min(count, MAX_GRID_COUNT)}>
        {files.map((f) => (
          <MessageFileCell
            key={f.fileId}
            onClick={() => void FilesCubit.startFileDownload(f.fileId)}
          >
            {previews[f.fileId] ? (
              <img
                src={previews[f.fileId]}
                alt={f.name ?? translate("leapChat.file.fallbackName")}
              />
            ) : isVisible && isLoading ? (
              <MessageFileSpinner />
            ) : (
              <MessageFileFallback>
                <DocumentIcon />
                <span className="fallback-name">
                  {f.name ?? translate("leapChat.file.fallbackName")}
                </span>
                {f.size != null && (
                  <span className="fallback-size">
                    {fileSizeString(f.size)}
                  </span>
                )}
              </MessageFileFallback>
            )}
          </MessageFileCell>
        ))}
      </MessageFilesGrid>
    </MessageFilesWrap>
  );
});

export default MessageFiles;
